/**
 * TenantMenuViewSwitcher — przełącznik WIDOKU menu w dolnej zonie sidebara tenanta
 * (drop-up popover, wzorzec skopiowany z `LayoutSwitcherFooter.tsx`).
 *
 * Zastępuje dawny inline `TenantModuleSwitcher` (pasywne nagłówki obszaru + wiersze
 * pozycji) modelem "jeden zakupiony moduł = jeden widok menu": klient wybiera KTÓREGO
 * modułu menu ma być wyświetlone w górnej zonie sidebara (`selectedModule.items`),
 * zamiast wybierać pojedynczą pozycję wewnątrz obszaru. Płaska lista modułów w
 * popoverze — BEZ grupowania po obszarze (obszar to grupowanie po stronie admina,
 * nie poziom nawigacji klienta — patrz komentarz przy `TenantSidebar`).
 *
 * Domyślny wybór: `crm_core` jeśli zakupiony, inaczej pierwszy moduł na liście.
 * Wybór persystowany w localStorage (`tenant.sidebar.selectedModuleCode`).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronUp } from 'lucide-react';

import type { InstalledMenuModuleGroup } from '@/components/Sidebar';
import { resolveIcon } from '@/components/Sidebar';

const SELECTED_MODULE_STORAGE_KEY_BASE = 'tenant.sidebar.selectedModuleCode';
const DEFAULT_MODULE_CODE = 'crm_core';

/**
 * Klucz localStorage jest PER-TENANT — panel SPA serwuje wielu tenantów z jednego
 * origin (subdomena per tenant), więc dyskryminujemy po hoście, żeby wybór widoku
 * menu nie przeciekał między tenantami w tej samej przeglądarce.
 */
function selectedModuleStorageKey(): string {
  const host = typeof window !== 'undefined' ? window.location.host : '';
  return `${SELECTED_MODULE_STORAGE_KEY_BASE}:${host}`;
}

/** Odczyt bezpieczny (prywatne okno / quota) — brak dostępu do localStorage nie krytyczne. */
function readStoredModuleCode(): string | null {
  try {
    return localStorage.getItem(selectedModuleStorageKey());
  } catch {
    return null;
  }
}

function writeStoredModuleCode(code: string): void {
  try {
    localStorage.setItem(selectedModuleStorageKey(), code);
  } catch {
    // Non-critical — brak persystencji między sesjami.
  }
}

/**
 * Stan wyboru modułu (widoku menu) + persystencja localStorage.
 *
 * Priorytet ustalania `selectedCode`:
 *   1. Zapisany kod z localStorage — o ile nadal istnieje na liście modułów.
 *   2. `preferredDefaultCode` (np. widok „Kadry" gdy firma ma moduł HR) — o ile na liście.
 *   3. `crm_core` — o ile zakupiony.
 *   4. Pierwszy moduł na liście.
 */
export function useTenantMenuViewSelection(
  modules: InstalledMenuModuleGroup[],
  preferredDefaultCode?: string,
) {
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  useEffect(() => {
    if (modules.length === 0) {
      setSelectedCode((prev) => (prev === null ? prev : null));
      return;
    }

    setSelectedCode((prev) => {
      if (prev && modules.some((m) => m.code === prev)) return prev;

      const stored = readStoredModuleCode();
      if (stored && modules.some((m) => m.code === stored)) return stored;

      if (preferredDefaultCode && modules.some((m) => m.code === preferredDefaultCode)) {
        return preferredDefaultCode;
      }

      const defaultModule = modules.find((m) => m.code === DEFAULT_MODULE_CODE);
      if (defaultModule) return defaultModule.code;

      return modules[0]?.code ?? null;
    });
  }, [modules, preferredDefaultCode]);

  function selectModule(code: string) {
    setSelectedCode(code);
    writeStoredModuleCode(code);
  }

  const selectedModule = selectedCode
    ? (modules.find((m) => m.code === selectedCode) ?? null)
    : null;

  return { selectedCode, selectedModule, selectModule };
}

export interface TenantMenuViewSwitcherProps {
  modules: InstalledMenuModuleGroup[];
  selectedCode: string | null;
  onSelect: (code: string) => void;
  /**
   * Menu customowe z kreatora (etap c, 2026-07-03) — sekcja pod menu modułów.
   * Wybór = preferencja usera (PUT /auth/menu-preference), sidebar renderuje
   * hierarchię własnego menu zamiast menu modułu. Puste menu odfiltrowane
   * po stronie callera (isEmpty).
   */
  customLayouts?: Array<{ id: string; name: string }>;
  /** ID aktywnego menu customowego (null = aktywny widok modułowy). */
  selectedCustomId?: string | null;
  onSelectCustom?: (id: string) => void;
}

export function TenantMenuViewSwitcher({
  modules,
  selectedCode,
  onSelect,
  customLayouts = [],
  selectedCustomId = null,
  onSelectCustom,
}: TenantMenuViewSwitcherProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Klik outside → zamyka popover.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (modules.length === 0) {
    return (
      <div className="border-t border-border-secondary px-4 py-2 text-xs text-text-muted">
        {t('tenant.sidebar.empty')}
      </div>
    );
  }

  const selectedModule = selectedCode
    ? (modules.find((m) => m.code === selectedCode) ?? null)
    : null;
  const selectedCustom = selectedCustomId
    ? (customLayouts.find((l) => l.id === selectedCustomId) ?? null)
    : null;
  // Aktywne menu customowe wygrywa prezentację triggera (moduł zostaje zapamiętany
  // w localStorage — powrót do widoku modułowego wraca do niego).
  const TriggerIcon = resolveIcon(selectedCustom ? 'PanelsTopLeft' : selectedModule?.icon);
  const triggerName = selectedCustom ? selectedCustom.name : (selectedModule?.name ?? '—');

  return (
    <div
      ref={containerRef}
      className="relative border-t border-border-secondary px-3 py-2"
      data-testid="tenant-menu-view-switcher"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-md border border-border-secondary bg-bg-primary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
        data-testid="tenant-menu-view-trigger"
        aria-label={t('tenant.sidebar.viewSwitcher.triggerLabel')}
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-2 text-left">
          <TriggerIcon className="h-4 w-4 flex-shrink-0 text-text-tertiary" aria-hidden="true" />
          <span className="min-w-0 truncate">
            <span className="block text-xs text-text-tertiary">
              {t('tenant.sidebar.viewSwitcher.triggerLabel')}
            </span>
            <span className="block truncate font-medium">{triggerName}</span>
          </span>
        </span>
        <ChevronUp
          className={`h-4 w-4 flex-shrink-0 text-text-tertiary transition-transform ${
            open ? '' : 'rotate-180'
          }`}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          className="absolute bottom-full left-3 right-3 z-40 mb-1 max-h-[60vh] overflow-y-auto rounded-md border border-border-secondary bg-bg-elevated py-1 shadow-lg"
          data-testid="tenant-menu-view-panel"
        >
          <div className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            {t('tenant.sidebar.viewSwitcher.title')}
          </div>
          {modules.map((mod) => {
            const isSelected = mod.code === selectedCode && !selectedCustom;
            const Icon = resolveIcon(mod.icon);
            return (
              <button
                key={mod.code}
                type="button"
                onClick={() => {
                  onSelect(mod.code);
                  setOpen(false);
                }}
                data-testid={`tenant-menu-view-option-${mod.code}`}
                aria-pressed={isSelected}
                className={[
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                  isSelected
                    ? 'bg-accent-50 text-accent-700 font-medium dark:bg-accent-900/30 dark:text-accent-300'
                    : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary',
                ].join(' ')}
              >
                {isSelected ? (
                  <Check className="h-3.5 w-3.5 flex-shrink-0 text-accent-600" aria-hidden="true" />
                ) : (
                  <span className="w-3.5 flex-shrink-0" />
                )}
                <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span className="truncate">{mod.name}</span>
              </button>
            );
          })}

          {/* Menu customowe z kreatora (etap c) — pod menu modułów. */}
          {customLayouts.length > 0 && onSelectCustom ? (
            <>
              <div className="my-1 border-t border-border-secondary" />
              <div className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                {t('tenant.sidebar.viewSwitcher.customHeader', {
                  defaultValue: 'Menu customowe',
                })}
              </div>
              {customLayouts.map((l) => {
                const isSelected = l.id === selectedCustomId;
                const Icon = resolveIcon('PanelsTopLeft');
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => {
                      onSelectCustom(l.id);
                      setOpen(false);
                    }}
                    data-testid={`tenant-menu-view-custom-${l.id}`}
                    aria-pressed={isSelected}
                    className={[
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                      isSelected
                        ? 'bg-accent-50 text-accent-700 font-medium dark:bg-accent-900/30 dark:text-accent-300'
                        : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary',
                    ].join(' ')}
                  >
                    {isSelected ? (
                      <Check
                        className="h-3.5 w-3.5 flex-shrink-0 text-accent-600"
                        aria-hidden="true"
                      />
                    ) : (
                      <span className="w-3.5 flex-shrink-0" />
                    )}
                    <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                    <span className="truncate">{l.name}</span>
                  </button>
                );
              })}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
